import { styled } from '@mui/material';
import { useId } from 'react';
import Box from 'shell/components/Box';
import Checkbox from 'shell/components/Checkbox';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const CardLabel = styled(Box, {
    shouldForwardProp: (prop) => !['$checked', '$disabled', '$error'].includes(prop),
})(({ theme, $checked, $disabled, $error }) => ({
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(5),
    border: `1px solid ${
        $error
            ? theme.palette.ensightful.error[700]
            : $checked
              ? theme.palette.ensightful.primary[500]
              : theme.palette.ensightful.neutral[300]
    }`,
    borderRadius: theme.spacing(4),
    background: $checked ? theme.palette.ensightful.primary[50] : theme.palette.ensightful.shades[0],
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.65 : 1,
    transition: theme.transitions.create(['background-color', 'border-color', 'outline']),
    '&:not([aria-disabled="true"])': {
        '&:hover': {
            borderColor: theme.palette.ensightful.neutral[500],
            background: theme.palette.ensightful.primary[50],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.neutral[300],
            background: theme.palette.ensightful.primary[100],
        },
    },
    '&:focus-within': {
        outline: `3px solid ${theme.palette.ensightful.secondary[400]}`,
        outlineOffset: '3px',
    },
    '&[aria-disabled="true"]': {
        borderColor: theme.palette.ensightful.neutral[400],
        background: theme.palette.ensightful.neutral[200],
    },
}));

const CheckboxCard = ({
    checked,
    disabled = false,
    error = false,
    icon,
    label,
    description,
    ariaLabel,
    labelSx,
    onChange,
    sx,
}) => {
    const labelId = useId();
    const descriptionId = useId();
    const resolvedAriaLabel = ariaLabel || (typeof label === 'string' ? label : undefined);

    return (
        <CardLabel
            component={'label'}
            aria-disabled={disabled || undefined}
            $checked={checked}
            $disabled={disabled}
            $error={error}
            sx={sx}
        >
            <Stack direction={'row'} alignItems={'center'} spacing={3}>
                {icon && (
                    <Box aria-hidden={'true'} sx={{ display: 'flex', flexShrink: 0 }}>
                        {icon}
                    </Box>
                )}
                <Text id={labelId} weight={'medium'} sx={{ flex: 1, minWidth: 0, ...labelSx }}>
                    {label}
                </Text>
                <Checkbox
                    appearance={'transparent-unchecked'}
                    checked={checked}
                    disabled={disabled}
                    error={error}
                    inputProps={{
                        ...(resolvedAriaLabel ? { 'aria-label': resolvedAriaLabel } : { 'aria-labelledby': labelId }),
                        ...(description ? { 'aria-describedby': descriptionId } : {}),
                    }}
                    onChange={onChange}
                    sx={{ p: 0, '& > input': { height: 24, width: 24 } }}
                />
            </Stack>
            {description && (
                <Text id={descriptionId} color={'light'}>
                    {description}
                </Text>
            )}
        </CardLabel>
    );
};

export default CheckboxCard;
